import { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Empty } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const dateLabel = (value) => (
  value
    ? new Date(value).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
    : "—"
);

function DuplicateGroup({ group, onDeleted }) {
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const toggle = (id) => {
    setSelected((current) => (
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id]
    ));
  };
  const deleteTicked = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/inventory/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} item${data.deleted === 1 ? "" : "s"} deleted`);
      onDeleted();
    } catch (error) {
      toast.error(errMsg(error));
      setDeleting(false);
    }
  };
  return (
    <section
      className="overflow-hidden rounded-lg border"
      data-testid={`inventory-duplicate-group-${group.items[0].id}`}
    >
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2.5">
        <span className="text-xs text-muted-foreground">
          {group.items.length} similar item names · {group.similarity}% match
        </span>
        <span className="text-xs text-muted-foreground">Tick records to delete</span>
      </div>
      <div className="divide-y">
        {group.items.map((item) => (
          <label
            key={item.id}
            className={`flex cursor-pointer items-start gap-3 px-4 py-3 ${
              selected.includes(item.id) ? "bg-destructive/5" : ""
            }`}
            data-testid={`inventory-duplicate-item-${item.id}`}
          >
            <Checkbox
              checked={selected.includes(item.id)}
              onCheckedChange={() => toggle(item.id)}
              className="mt-1"
              aria-label={`Delete ${item.name}`}
              data-testid={`inventory-duplicate-delete-${item.id}`}
            />
            <div className="min-w-0 flex-1">
              <p className="font-medium">{item.name}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {[item.category, item.item_type, item.location].filter(Boolean).join(" · ") || "No details"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {item.quantity ?? "—"} {item.units} · Added {dateLabel(item.date_added)}
              </p>
            </div>
          </label>
        ))}
      </div>
      <div className="border-t px-4 py-3">
        <Button
          size="sm"
          variant="destructive"
          disabled={deleting || selected.length === 0}
          onClick={deleteTicked}
          data-testid={`inventory-duplicate-delete-ticked-${group.items[0].id}`}
        >
          <Trash2 className="mr-1.5 h-3.5 w-3.5" />
          Delete ticked{selected.length ? ` (${selected.length})` : ""}
        </Button>
      </div>
    </section>
  );
}

export function InventoryDuplicatesDialog({ onClose, onChanged }) {
  const [groups, setGroups] = useState(null);
  const load = useCallback(() => {
    api
      .get("/inventory-duplicates")
      .then(({ data }) => setGroups(data.groups))
      .catch((error) => toast.error(errMsg(error)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const deleted = () => {
    load();
    onChanged();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[90vh] max-w-3xl overflow-y-auto"
        data-testid="inventory-duplicates-dialog"
      >
        <DialogHeader>
          <DialogTitle>Possible inventory duplicates</DialogTitle>
          <DialogDescription>
            Similar item names are shown together. Tick only records to delete; unticked items stay.
          </DialogDescription>
        </DialogHeader>
        {groups === null ? (
          <p className="py-6 text-sm text-muted-foreground" data-testid="inventory-duplicates-loading">
            Looking for duplicates…
          </p>
        ) : groups.length === 0 ? (
          <Empty>No likely duplicates found.</Empty>
        ) : (
          <div className="space-y-3" data-testid="inventory-duplicates-list">
            {groups.map((group) => (
              <DuplicateGroup key={group.key} group={group} onDeleted={deleted} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}